import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import Link from 'next/link';
import type { ArticleReference } from 'domain-model/types/oms';
import {
  Row,
  Col,
  Table,
  UncontrolledDropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from 'reactstrap';
import PropTypes from 'prop-types';
import InfiniteScroll from 'react-infinite-scroller';
import type { ShopConfig } from 'domain-model/types/supplier-gateway';
import domainModel from 'domain-model';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';

import AuthenticatedWrapper from 'flaivy-react/components/AuthenticatedWrapper';

import _ from 'lodash';
import ArticleCard from './ArticleCard';
import ArticleListCard from './ArticleListCard';
import {
  ArticleListController,
  type ArticleListControllerConfig,
} from './articleListController';
import FilterPanel from '../filters/FilterPanel';

//@ts-ignore
import ConnectedArticleQuantityInputJustButtons from 'flaivy-react/components/inputs/ConnectedArticleQuantityInputJustButtons';
import Image from 'flaivy-react/components/Image';
import DiscountPriceSpan from 'flaivy-react/components/DiscountPriceSpan';
import { useRouter } from 'next/router';
import LoginToShowPrices from '../LoginToShowPrices';
import FilterPanelModal from 'components/filters/FilterPanelModal';

function GroupedArticleList({
  groupedData,
  controller,
}: {
  groupedData: Map<string, ArticleReference[]>;
  controller: ArticleListController;
}) {
  return (
    <>
      {Array.from(groupedData.entries()).map(key => {
        const [groupLabel, groupArticles] = key;
        return (
          <>
            {groupLabel && (
              <Col xs={12}>
                <h3
                  color="Link"
                  className="ps-2 pe-0 text-muted link-animated link-animated-light fw-light"
                >
                  {domainModel.displayPipeString(groupLabel)}
                </h3>
              </Col>
            )}

            {groupArticles.map(item =>
              controller.getDisplayType() === 'Card' ? (
                <Col xs={12} md={4} key={item.id} className="p-1 g-0">
                  <ArticleCard article={item} />
                </Col>
              ) : (
                <ArticleListCard key={item.id} article={item} />
              ),
            )}
          </>
        );
      })}
    </>
  );
}

GroupedArticleList.propTypes = {
  groupedData: PropTypes.object.isRequired,
};

function DisplayValue({
  value,
  type,
  entity,
  column,
}: {
  value: any;
  type: string;
  entity: ArticleReference;
  column: string;
}) {
  if (!value) return null;
  if (column === 'volume') {
    return (
      <span>
        {domainModel.article.getViewModel(entity).displayOrderUnitSize}
      </span>
    );
  }
  switch (type) {
    case 'number':
      return <span>{value.toFixed(2)}</span>;
    case 'boolean':
      return <span>{value ? 'Ja' : ''}</span>;
    case 'croppedS3image':
      return (
        <Image src={`${value}`} alt="Article thumbnail" resX={50} resY={50} />
      );
    case 'pipeString':
      return (
        <span>
          {(
            (_.last(
              domainModel.displayPipeString(value).split('>'),
            ) as string) || ''
          ).trim()}
        </span>
      );
    case 'object':
      return <span>{value.name}</span>;
    case 'salesPrice':
      return (
        <AuthenticatedWrapper>
          <>
            <DiscountPriceSpan pricedArticle={entity} />
          </>
        </AuthenticatedWrapper>
      );
    default:
      return <span>{value}</span>;
  }
}

DisplayValue.propTypes = {
  value: PropTypes.any.isRequired,
  type: PropTypes.string.isRequired,
};

const typeMap: { [key: string]: string } = {
  id: 'string',
  name: 'string',
  price: 'salesPrice',
  salesPrice: 'salesPrice',
  image: 'croppedS3image',
  isVegan: 'boolean',
  isEco: 'boolean',
  alcoholPercentage: 'number',
  grapes: 'string',
  vintage: 'string',
  brand: 'object',
  origin: 'pipeString',
  productCategory: 'pipeString',
  articleNumber: 'string',
  volume: 'string',
  colliSize: 'string',
};

const linkColumn: { [key: string]: boolean } = {
  name: true,
  image: true,
};

function TableArticleList({
  groupedData,
  controller,
}: {
  groupedData: Map<string, ArticleReference[]>;
  controller: ArticleListController;
}) {
  const columns = controller.getColumns();
  const [stickyHeaderTop, setStickyHeaderTop] = useState(160);
  const headerRef = useRef<HTMLTableSectionElement>(null);
  const tableRef = useRef<HTMLTableSectionElement>(null);
  const [headerHeight, setHeaderHeight] = useState(55);

  useEffect(() => {
    const handleResize = () => {
      if (headerRef.current && tableRef.current) {
        // When we change layout of the main elements on the page to not
        // use display flex to set the footer (first render is no longer in the middle of the page)
        // then we can calculate the top only based on the headerRect

        const headerRect = headerRef.current.getBoundingClientRect();

        const stickyHeaderTop = tableRef.current!.getBoundingClientRect().top;
        setStickyHeaderTop(stickyHeaderTop);
        setHeaderHeight(headerRect.height);
      }
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize); // header changes size based on table content on scroll
    handleResize();
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize);
    };
  }, []);

  function headerLabel(column: string): React.ReactNode {
    if (column === 'salesPrice') return 'Pris';
    if (column === 'name') return 'Namn';
    if (column === 'shortName') return 'Namn';

    return column === 'id' || column === 'image'
      ? ''
      : domainModel.getTranslation('article', column);
  }
  const stickyRowTop = stickyHeaderTop + headerHeight;

  return (
    <>
      {/* @ts-ignore */}
      <Table hover ref={tableRef}>
        <thead
          ref={headerRef}
          style={{ position: 'sticky', top: stickyHeaderTop, zIndex: 1000 }}
        >
          <tr className="bg-light mb-0 border-bottom-1">
            {columns.map(column => (
              <th
                key={column}
                style={{
                  width: typeMap[column] === 'croppedS3image' ? '0' : '',
                }}
                className={`${typeMap[column] === 'number' ? 'text-end' : ''} `}
                onClick={() => controller.toggleSort(column)}
              >
                {headerLabel(column)}
                {controller.getSort().includes(column) && (
                  <span>
                    {controller.getSort().startsWith('-') ? (
                      <>
                        {' '}
                        <FontAwesomeIcon icon={faChevronDown} />
                      </>
                    ) : (
                      <>
                        {' '}
                        <FontAwesomeIcon icon={faChevronUp} />
                      </>
                    )}
                  </span>
                )}
              </th>
            ))}
            <th></th>
          </tr>
        </thead>
        <tbody>
          {Array.from(groupedData.entries()).map(key => {
            const [groupLabel, groupArticles] = key;
            return (
              <>
                <tr
                  className="align-middle bg-light border-top border-bottom"
                  style={{
                    position: 'sticky',
                    top: stickyRowTop,
                    zIndex: 1000,
                  }}
                >
                  <td colSpan={7}>
                    <strong>{domainModel.displayPipeString(groupLabel)}</strong>
                  </td>
                </tr>
                {groupArticles.map(item => (
                  <tr
                    key={item.id + '12323' + _.uniqueId()}
                    className="align-middle"
                  >
                    {columns.map(column => (
                      <td
                        key={column + item.id + '3'}
                        className={`${
                          typeMap[column] === 'number' ? 'text-end' : ''
                        }`}
                        style={{
                          width:
                            typeMap[column] === 'croppedS3image' ? '0' : '',
                        }}
                      >
                        {linkColumn[column] ? (
                          <Link href={`/articles/${item.id}`}>
                            <DisplayValue
                              value={item[column as keyof typeof item]}
                              type={typeMap[column] || ''}
                              entity={item}
                              column={column}
                            />
                          </Link>
                        ) : (
                          <DisplayValue
                            value={item[column as keyof typeof item]}
                            type={typeMap[column] || ''}
                            entity={item}
                            column={column}
                          />
                        )}
                      </td>
                    ))}
                    <td className="text-end w-auto">
                      <LoginToShowPrices>
                        <ConnectedArticleQuantityInputJustButtons
                          article={item}
                        />
                      </LoginToShowPrices>
                    </td>
                  </tr>
                ))}
              </>
            );
          })}
        </tbody>
      </Table>
    </>
  );
}

const SortButton = ({ controller }: { controller: ArticleListController }) => {
  const getSortButtonText = () => {
    switch (controller.getSort()) {
      case '-popularity':
        return 'Sortera på: Popularitet';
      case '-salesPrice':
        return 'Sortera på: Pris högt till lågt';
      case 'salesPrice':
        return 'Sortera på: Pris lågt till högt';
      case '-currentNewPeriodStarted,-isOrderable':
        return 'Sortera på: Nyheter';
      case '-articleNumber':
        return 'Artikelnummer';
      default:
        return 'Sortera';
    }
  };
  return (
    <UncontrolledDropdown>
      <DropdownToggle className="styled-btn" outline color="dark">
        <div className="styled-btn-text">{getSortButtonText()}</div>
      </DropdownToggle>
      <DropdownMenu end>
        <DropdownItem
          onClick={() =>
            controller.setSort('-currentNewPeriodStarted,-isOrderable')
          }
        >
          Nyheter
        </DropdownItem>
        <DropdownItem onClick={() => controller.setSort('-popularity')}>
          Popularitet
        </DropdownItem>
        <DropdownItem onClick={() => controller.setSort('salesPrice')}>
          Pris: lågt till högt
        </DropdownItem>
        <DropdownItem onClick={() => controller.setSort('-salesPrice')}>
          Pris: högt till lågt
        </DropdownItem>
        <DropdownItem onClick={() => controller.setSort('-articleNumber')}>
          Artikelnummer
        </DropdownItem>
      </DropdownMenu>
    </UncontrolledDropdown>
  );
};

function ArticleList({
  articleListControllerConfig,
  shopConfig,
}: {
  articleListControllerConfig: ArticleListControllerConfig;
  shopConfig: ShopConfig;
}) {
  articleListControllerConfig.router = useRouter();

  const newController = ArticleListController.create(
    articleListControllerConfig,
  );
  const [controller, setController] = useState(newController);
  useEffect(() => {
    const waitForUpdates = async () => {
      const c = await controller.nextController();
      setController(c);
    };
    waitForUpdates();
  }, [controller]);
  useEffect(() => {
    controller.updateIfNewQuery(articleListControllerConfig);
  }, [
    articleListControllerConfig.router.query.querySpecification,
    articleListControllerConfig,
  ]);

  const [stickyHeaderTop, setStickyHeaderTop] = useState(
    domainModel.shopConfig.getNavbarHeight(shopConfig),
  );
  const headerRef = useRef<HTMLTableSectionElement>(null);

  useEffect(() => {
    const handleResize = () => {
      if (headerRef.current) {
        const stickyHeaderTop = headerRef.current.getBoundingClientRect().top;
        setStickyHeaderTop(stickyHeaderTop);
      }
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize); // header changes size based on table content on scroll
    handleResize();
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize);
    };
  }, []);

  const loadNextPage = async () => {
    if (!controller.isLoading()) {
      await controller.fetchData();
    }
  };

  let content = null;

  if (
    controller.getDisplayType() === 'Card' ||
    controller.getDisplayType() === 'PurchasingTable'
  ) {
    const groupedData = controller.getGroupedData();
    content = (
      <GroupedArticleList groupedData={groupedData} controller={controller} />
    );
  }

  if (controller.getDisplayType() === 'Table') {
    const groupedData =
      controller.getGroupingKey() !== ''
        ? controller.getGroupedData()
        : new Map([['', controller.getData()]]);
    content = (
      <TableArticleList groupedData={groupedData} controller={controller} />
    );
  }

  return (
    <>
      {controller.getDisplayType() === 'Card' && (
        <Row className="mt-2 pe-1">
          <Col xs="12" className="text-end d-none d-lg-block">
            <SortButton controller={controller} />
          </Col>
        </Row>
      )}
      <Row className="mt-2">
        {controller.showFilterPanel() && (
          <Col xs={12} className="d-lg-none">
            <Row className="justify-content-end pe-1">
              <Col xs="auto" className=" mb-2">
                <FilterPanelModal
                  shopConfig={shopConfig}
                  onChange={newQuery =>
                    controller.updateQuerySpecification(newQuery)
                  }
                  querySpecification={controller.getQuerySpecification()}
                  articleListConfig={articleListControllerConfig}
                />
              </Col>
              {controller.getDisplayType() === 'Card' && (
                <Col xs="auto" className="text-end">
                  <SortButton controller={controller} />
                </Col>
              )}
            </Row>
          </Col>
        )}
        {controller.showFilterPanel() && (
          <Col xs={3} className="d-none d-lg-block p-1 pe-2">
            <FilterPanel
              ref={headerRef}
              style={{ position: 'sticky', top: stickyHeaderTop }}
              onChange={newQuery =>
                controller.updateQuerySpecification(newQuery)
              }
              querySpecification={controller.getQuerySpecification()}
              hasValueCallback={controller.filterHasValueCallback}
              shopConfig={shopConfig}
              hideShortcuts={true}
              articleListConfig={articleListControllerConfig}
            />
          </Col>
        )}
        <Col xs={12} lg={controller.showFilterPanel() ? 9 : 12}>
          <InfiniteScroll
            pageStart={1}
            loadMore={async () => await loadNextPage()}
            hasMore={controller.hasMoreData()}
          >
            <Row className="g-0">{content}</Row>
          </InfiniteScroll>
        </Col>
      </Row>
    </>
  );
}

const mapStateToProps = (state: any) => ({
  shopConfig: state.shopConfig,
});

export default connect(mapStateToProps)(ArticleList);
